import React, { Component, ReactNode } from 'react';
import LanguageSelectorLayoutEnum from '@inter-ikea-kompis/component-language-selector/lib/enums/LanguageSelectorLayoutEnum';
import MobileMeasurementsContainer from '../../containers/MobileMeasurementsContainer';
import { ArrowLeftIcon } from '@inter-ikea-kompis/icons';
import ArrowLeftIconPaths from '@ingka/ssr-icon/paths/arrow-left';
import ArrowRightIconPaths from '@ingka/ssr-icon/paths/arrow-right';
import CrossIconPaths from '@ingka/ssr-icon/paths/cross';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { localeService } from '../../services';
import OpenDesignContainer from '../../containers/OpenDesignContainer';
import SkapaButton from '@ingka/button';
import {
	KompisPrompt,
	KompisText,
	KompisIcon,
	KompisHyperlink,
	KompisLanguageSelector,
} from '@inter-ikea-kompis/react-components';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import {
	StyledHeader,
	StyledShowActionButtonsLink,
	StyledLanguageSelector,
	BackButtonWrapper,
	NavigationWrapper,
	ButtonContainer,
	GalleryButtonWrapper,
	AddFakeDataButton,
	StyledUpptackaButton,
	getIconButtonStyle,
	getHeaderTextStyle,
	getIconStyle,
} from './HeaderStyle';
import {
	Settings,
	DeviceType,
	Orientation,
	WriteDirection,
	AppView,
	Platform,
	SofaRangeName,
	Lightbox,
} from 'sofa-reborn-react';
import { CoverOnlySwitch } from '../CoverOnlySwitch';
import type { LanguageSelectorModalEnum } from '@inter-ikea-kompis/component-language-selector';
import { IDexfSettings, ITranslations } from '@inter-ikea-kompis/types';

type Props = {
	settings: Settings;
	deviceType: DeviceType;
	orientation: Orientation;
	appView: AppView;
	writeDirection: WriteDirection;
	platform: Platform;
	toolsBottom: number;
	showArea: boolean;
	hasVisitScene: boolean;
	showMeasurements: boolean;
	showScreensaver: boolean;
	showActionButtons?: boolean;
	sofaRangeName: SofaRangeName;
	currentLightbox: Lightbox;
	enableFakeData: boolean | null;
	noSummaryPageBackButton: boolean | null;
	showIpexGalleryButton: boolean;
	showConfigurationAssistant: boolean;
	configurationString: string | null;
	updateConfiguration: boolean;
	isInUpptackaPlatform: boolean;
	kompisSettings?: IDexfSettings | null;
	kompisTranslations?: ITranslations | null;
	onSummaryPage: boolean;
	visibleModal: LanguageSelectorModalEnum | null | undefined;
	visibleIpexGalleryPrompt: boolean;
	fromNavigator: string | null;
	navigatorUrl: string;
	onBackToScene?: () => void;
	onStartAgain?: () => void;
	onShowActionButtons?: () => void;
	onModalOpen?: (e: CustomEvent) => void;
	onModalClose?: () => void;
	onChangeLanguage?: (locale: string) => void;
	onCloseOpenDesign?: () => void;
	onAddFakeDataClick?: () => void;
	onUpptackaCloseButtonClicked?: () => void;
	onIpexGalleryButtonClick?: () => void;
	onIpexGalleryPromptButtonClick?: (event) => void;
};

/**
 * React component.
 *
 */
export default class Header extends Component<Props> {
	/**
	 * React render method.
	 *
	 * @returns React node.
	 */
	public render(): ReactNode {
		const { props } = this;
		const {
			onStartAgain,
			onBackToScene,
			onShowActionButtons,
			onChangeLanguage,
			onCloseOpenDesign,
			onAddFakeDataClick,
			onUpptackaCloseButtonClicked,
			onModalOpen,
			onModalClose,
			onIpexGalleryButtonClick,
			onIpexGalleryPromptButtonClick,
		} = props;
		const isKiosk = props.platform === 'kiosk';
		return (
			<StyledHeader
				deviceType={props.deviceType}
				orientation={props.orientation}
				writeDirection={props.writeDirection}
				platform={props.platform}
				addMarginTop={
					props.platform !== 'kiosk' || props.currentLightbox !== 'product-intro-gallery'
				}
			>
				<NavigationWrapper
					isKiosk={props.platform === 'kiosk'}
					onClick={(): void => onCloseOpenDesign && onCloseOpenDesign()}
				>
					{props.isInUpptackaPlatform && props.appView === 'build-view' ? (
						<StyledUpptackaButton
							writeDirection={props.writeDirection}
							isPIG={props.currentLightbox === 'product-intro-gallery'}
							addBorder={!props.hasVisitScene}
						>
							<SkapaButton
								type={'secondary'}
								ssrIcon={CrossIconPaths}
								small={true}
								iconOnly
								onClick={(): void => onUpptackaCloseButtonClicked && onUpptackaCloseButtonClicked()}
							/>
						</StyledUpptackaButton>
					) : null}
					{props.isInUpptackaPlatform &&
					props.hasVisitScene &&
					props.currentLightbox === 'product-intro-gallery' ? (
						<StyledUpptackaButton
							writeDirection={props.writeDirection}
							isPIG={props.currentLightbox === 'product-intro-gallery'}
							addBorder={true}
						>
							<SkapaButton
								type={'secondary'}
								ssrIcon={props.writeDirection === 'ltr' ? ArrowRightIconPaths : ArrowLeftIconPaths}
								small={true}
								iconOnly
								onClick={(): void => onBackToScene && onBackToScene()}
								aria-label={localeService.getTranslation('sprGalleryGoToBuildView')}
							/>
						</StyledUpptackaButton>
					) : null}
					{props.currentLightbox !== 'product-intro-gallery' && !props.noSummaryPageBackButton ? (
						<BackButtonWrapper
							id="backButton"
							writeDirection={props.writeDirection}
							platform={props.platform}
							deviceType={props.deviceType}
							isUpptacka={props.isInUpptackaPlatform}
						>
							<SkapaButton
								iconOnly
								ssrIcon={props.writeDirection === 'ltr' ? ArrowLeftIconPaths : ArrowRightIconPaths}
								small={props.deviceType === 'mobile' || props.isInUpptackaPlatform ? true : false}
								type={'secondary'}
								style={getIconButtonStyle(
									props.platform,
									props.writeDirection,
									props.deviceType,
									props.orientation
								)}
								href={props.fromNavigator ? props.navigatorUrl : undefined}
								onClick={
									props.fromNavigator ? undefined : (): void => onStartAgain && onStartAgain()
								}
								aria-label={
									props.onSummaryPage
										? localeService.getTranslation('backToPlanningLabel')
										: localeService.getTranslation('backToGalleryLabel')
								}
							/>
						</BackButtonWrapper>
					) : null}

					{props.currentLightbox === 'product-intro-gallery' && props.showIpexGalleryButton ? (
						<>
							<KompisPrompt
								theme={SkapaTheme}
								fullscreen={true}
								heading={localeService.getTranslation('ipexGalleryExitDialogueHeadline')}
								primaryButtonLabel={localeService.getTranslation(
									'ipexGalleryExitDialogueContinueButton'
								)}
								secondaryButtonLabel={localeService.getTranslation(
									'ipexGalleryExitDialogueExitButton'
								)}
								compact={true}
								visible={props.visibleIpexGalleryPrompt}
								onButtonClick={(event) =>
									onIpexGalleryPromptButtonClick && onIpexGalleryPromptButtonClick(event)
								}
							>
								<KompisText theme={SkapaTheme}>
									{localeService.getTranslation('ipexGalleryExitDialogueBody')}
								</KompisText>
							</KompisPrompt>
							<GalleryButtonWrapper
								platform={props.platform}
								writeDirection={props.writeDirection}
								showIpexGalleryButton={props.showIpexGalleryButton}
								onClick={(): void => onIpexGalleryButtonClick && onIpexGalleryButtonClick()}
							>
								<KompisIcon
									theme={SkapaTheme}
									icon={ArrowLeftIcon || ''}
									flipIconRtl={true}
									// @ts-expect-error Kompis elemenets support style but it isn't exposed
									style={getIconStyle(props.writeDirection, !props.showIpexGalleryButton)}
								/>
							</GalleryButtonWrapper>
						</>
					) : null}

					{isKiosk && !props.noSummaryPageBackButton && (
						<KompisText
							theme={SkapaTheme}
							type={ThemeFontStyleTypeEnum.headingLarge}
							bold={true}
							// @ts-expect-error Kompis elemenets support style but it isn't exposed
							style={getHeaderTextStyle(
								props.writeDirection,
								props.currentLightbox,
								props.showIpexGalleryButton,
								props.isInUpptackaPlatform
							)}
						>
							{props.sofaRangeName}
						</KompisText>
					)}
					{props.enableFakeData && (
						<AddFakeDataButton>
							<SkapaButton
								type={'primary'}
								text={'Add Fake Data'}
								onClick={(): void => onAddFakeDataClick && onAddFakeDataClick()}
							/>
						</AddFakeDataButton>
					)}
					{props.showConfigurationAssistant && props.configurationString && (
						<KompisText
							theme={SkapaTheme}
							type={ThemeFontStyleTypeEnum.headingSmall}
							bold={true}
							// @ts-expect-error Kompis elemenets support style but it isn't exposed
							style={{
								overflow: 'hidden',
								width: '100px',
								height: '50px',
								paddingLeft: '20px',
								backgroundColor: props.updateConfiguration
									? SkapaTheme.color.semantic.positive
									: SkapaTheme.color.neutral.white,
							}}
						>
							{props.configurationString}
						</KompisText>
					)}
					{props.appView === 'build-view' && props.currentLightbox !== 'product-intro-gallery' && (
						<CoverOnlySwitch />
					)}
				</NavigationWrapper>

				<ButtonContainer>
					{isKiosk && props.kompisSettings && props.kompisTranslations ? (
						<StyledLanguageSelector
							writeDirection={props.writeDirection}
							onClick={(): void => onCloseOpenDesign && onCloseOpenDesign()}
						>
							{!props.showScreensaver && (
								<KompisLanguageSelector
									locale={localeService.getLocale()}
									settings={props.kompisSettings}
									translations={props.kompisTranslations.kompis}
									theme={SkapaTheme}
									layout={LanguageSelectorLayoutEnum.popover}
									visibleModal={props.visibleModal}
									onModalOpen={(e: CustomEvent): void => onModalOpen && onModalOpen(e)}
									onModalClose={(): void => onModalClose && onModalClose()}
									onChange={(e: CustomEvent): void =>
										onChangeLanguage && onChangeLanguage(e.detail.locale.replace('-', '_'))
									}
									// @ts-expect-error Kompis elemenets support style but it isn't exposed
									style={{
										zIndex: props.onSummaryPage ? 11 : 5,
									}}
								></KompisLanguageSelector>
							)}
						</StyledLanguageSelector>
					) : null}
					{props.currentLightbox === 'product-intro-gallery' ? <OpenDesignContainer /> : null}
				</ButtonContainer>
				{props.showArea && props.deviceType === 'mobile' && props.showMeasurements && (
					<MobileMeasurementsContainer />
				)}
				{props.deviceType === 'mobile' &&
				props.appView === 'build-view' &&
				props.currentLightbox !== 'product-intro-gallery' ? (
					<StyledShowActionButtonsLink
						orientation={props.orientation}
						writeDirection={props.writeDirection}
						toolsBottom={props.toolsBottom}
					>
						<KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.bodyMedium} bold={true}>
							<KompisHyperlink
								theme={SkapaTheme}
								onLinkClick={(): void => onShowActionButtons && onShowActionButtons()}
								text={
									props.showActionButtons
										? localeService.getTranslation('hideTools')
										: localeService.getTranslation('showTools')
								}
							></KompisHyperlink>
						</KompisText>
					</StyledShowActionButtonsLink>
				) : null}
			</StyledHeader>
		);
	}
}
